/**
 * API Service for Notematic
 * Handles communication with Azure Functions backend
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://fn-notematic-signup.azurewebsites.net';

export interface SignupResponse {
  message: string;
}

export interface SignupError {
  error: string;
}

/**
 * Sign up for early access
 * @param email - User's email address
 * @returns Promise with signup response
 */
export async function signupForEarlyAccess(email: string): Promise<SignupResponse> {
  const response = await fetch(`${API_BASE_URL}/api/signup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const errorData: SignupError = await response.json().catch(() => ({
      error: 'Failed to sign up. Please try again later.',
    }));
    throw new Error(errorData.error);
  }

  return response.json();
}

/**
 * Confirm email signup
 * @param token - Confirmation token from email
 * @returns Promise with confirmation response
 */
export async function confirmEmail(token: string): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE_URL}/api/confirm?token=${encodeURIComponent(token)}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: 'Invalid or expired confirmation link.',
    }));
    throw new Error(errorData.error);
  }

  return response.json();
}

export interface ContactFormData {
  name: string;
  email: string;
  organisation: string;
  message: string;
}

export interface ContactResponse {
  message: string;
  submissionId: string;
}

/**
 * Submit contact form
 * @param data - Contact form data
 * @returns Promise with contact response
 */
export async function submitContactForm(data: ContactFormData): Promise<ContactResponse> {
  const response = await fetch(`${API_BASE_URL}/api/contact`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: 'Failed to submit contact form. Please try again later.',
    }));
    throw new Error(errorData.error);
  }

  return response.json();
}

